import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useAtom } from 'jotai';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { StudyLocalizationStatus } from '@axon/rosetta-sdk';
import { ActivityOrigin, useActivitySourceStatus } from 'libs.ecoa_shared';
import { FirebaseAppContext } from 'libs.firebase_react';
import { AssetTypes, LocalizationOrigin } from 'libs.localization.common';
import { useLocalization, useLocalizationExport, useLocalizationResource } from 'libs.localization.react';
import { Badge, H3, Locale, Spinner, Text, Tooltip, useLocalizeMessage } from 'libs.nucleus';
import { DeviceSelector, selectedDeviceAtom } from 'libs.preview';
import { ButtonWithTooltip, PageHeader } from 'libs.react';
import { TranslationsOutOfSyncBanner } from '../../components';
import { AIGeneratedBanner } from '../../components/ai_generated_banner/AI_generated_banner.component';
import { AssetsInfoPanel } from '../../components/assets_info_panel';
import { BuildVersion } from '../../components/locale_matrix/build_version.component';
import { ActivityReviewScreenList } from '../../components/review_screenlist';
import { TranslationWorkbenchContext } from '../../contexts';
import { useLibraryDetails, useLocalizationExportV2 } from '../../hooks';
import { deviceList } from '../../utils/device_list';
import { isTranslationV2Enabled } from '../../utils/env_utils';
export const ActivityReviewView = ({ assetId, studyId }) => {
    const translate = useLocalizeMessage();
    const { locale = Locale.en_US } = useParams();
    const [selectedDevice] = useAtom(selectedDeviceAtom);
    const { studyLocalizationStatus, libraryAssets } = useContext(TranslationWorkbenchContext);
    const isStudyinDraft = studyLocalizationStatus !== StudyLocalizationStatus.READY;
    const { logEvent } = useContext(FirebaseAppContext);
    const { isLoading, data: library } = useLibraryDetails(assetId);
    // autobuild activities return in data.data.data.activity vs ecoa return in data.data.data format
    // TODO remove autobuild activities return in data.data.data.activity
    const resource = library?.data.data.data.activity || library?.data.data.data;
    const buildVersion = library?.data.data.version;
    const _activity = Object.values(libraryAssets).find((asset) => asset.id === assetId);
    const resourceDisplayname = resource?.short_name;
    const { exportLocalization, isFetching } = useLocalizationExport();
    const { mutate: exportLocalizationV2, isPending: isExportingV2 } = useLocalizationExportV2();
    const { activityOrigin, sourceLink, sourceActivityName, sourceActivityVersion } = useActivitySourceStatus(assetId);
    const isNewActivity = activityOrigin === ActivityOrigin.ORIGINAL;
    const [localizationResourceEntry, setLocalizationResourceEntry] = useState(null);
    const [isLoadingSourceEntry, setIsLoadingSourceEntry] = useState(false);
    const asset = useMemo(() => (resource ? { activity: resource } : undefined), [resource]);
    const { localizationEntry, localization, isLoading: isLocalizationLoading } = useLocalization(asset);
    const localeVersion = localizationEntry?.supported_locales?.[locale]?.version;
    const localizationOrigin = localizationEntry?.supported_locales?.[locale].localization_origin;
    const currentVersionStatus = _activity?.currentVersionStatus?.[locale];
    const isEnUsAIAssistedBadge = locale === Locale.en_US && resource?.is_ai_generated;
    const { localizationResourceEntry: localizationResourceEntryResource } = useLocalizationResource(asset, locale, localeVersion);
    const currentBaseLocaleVersion = localizationEntry?.supported_locales?.[localizationEntry?.base_locale || Locale.en_US]?.version;
    const currentResourceBaseLocaleVersion = localizationResourceEntryResource?.base_locale_version;
    const isBaseLocaleInSync = currentBaseLocaleVersion === currentResourceBaseLocaleVersion;
    const isLocaleAIAssistedBadge = currentVersionStatus?.origin &&
        [LocalizationOrigin.AI_TRANSLATION, LocalizationOrigin.AI_MIGRATION].includes(currentVersionStatus?.origin) &&
        locale !== Locale.en_US;
    const showAIAssistedBadge = isEnUsAIAssistedBadge || isLocaleAIAssistedBadge;
    useEffect(() => {
        logEvent('translations_sr_device_size', {
            studyId,
            assetId,
            ...selectedDevice,
        });
    }, [selectedDevice]);
    // Fetch source localization resource entry
    useEffect(() => {
        if (!isNewActivity && localization && locale && currentVersionStatus?.version) {
            setIsLoadingSourceEntry(true);
            localization
                .getDatabaseLocalizationResourceEntry(locale, currentVersionStatus.version)
                .then((entry) => {
                setLocalizationResourceEntry(entry || null);
            })
                .catch((error) => {
                console.error('Error fetching source localization resource entry:', error);
            })
                .finally(() => {
                setIsLoadingSourceEntry(false);
            });
        }
    }, [isNewActivity, localization, locale, currentVersionStatus?.version]);
    // @TODO check pathname and add correct breadcrumb study vs library
    const breadcrumbPages = useMemo(() => [
        { name: translate('Translations'), path: `/studies/${studyId}/translations` },
        {
            name: resourceDisplayname || '',
            path: `/studies/${studyId}/translations/${assetId}?assetType=${AssetTypes.ACTIVITY}`,
        },
        { name: locale || '', path: `/studies/${studyId}/translations/${assetId}/review/${locale}` },
    ], [studyId, assetId, resourceDisplayname]);
    const info = useMemo(() => {
        const baseInfo = [
            { title: translate('Short Name'), description: resource?.short_name || '' },
            {
                title: translate('Display Name'),
                description: resource?.full_name || '',
            },
            {
                title: translate('Build version'),
                description: _jsx(BuildVersion, { version: buildVersion }),
            },
        ];
        return baseInfo;
    }, [
        translate,
        resource?.short_name,
        resource?.full_name,
        buildVersion,
        isNewActivity,
        sourceLink,
        sourceActivityName,
        sourceActivityVersion,
        localizationResourceEntry?.localization_origin_details,
        isLoadingSourceEntry,
    ]);
    const defaultDevice = useMemo(() => {
        let defaultDevice;
        const type = resource?.activity_metadata?.instrument_type;
        switch (type) {
            case 'clinro':
            case 'workflow':
                defaultDevice = deviceList.desktopSmall;
                break;
            case 'pro':
            case 'obSRO':
                defaultDevice = deviceList.iosSmall;
                break;
            default:
                defaultDevice = deviceList[Object.keys(deviceList)[0]];
        }
        return defaultDevice;
    }, [resource]);
    const handleExport = useCallback(() => {
        if (resource && localization) {
            const viewport = Object.entries(deviceList).find(([, value]) => value.device === selectedDevice.device)?.[0];
            exportLocalization({
                resources: ['screens'],
                locales: [locale],
                ...(viewport && { viewport }),
                assets: { [localization.getId()]: [`${locale}~${currentVersionStatus?.version}`] },
            });
            logEvent('translations_screen_report_downloaded', {
                context: 'study',
                type: AssetTypes.ACTIVITY,
                id: assetId,
                locale,
            });
        }
    }, [resource, locale, selectedDevice, currentVersionStatus, localization]);
    const handleExportV2 = useCallback(() => {
        if (resource && localization) {
            const viewport = Object.entries(deviceList).find(([, value]) => value.device === selectedDevice.device)?.[0];
            exportLocalizationV2({
                resources: ['screens'],
                assets: { [localization.getId()]: [`${locale}~${currentVersionStatus?.version}`] },
                ...(viewport && { viewport }),
            });
            logEvent('translations_screen_report_downloaded', {
                context: 'study',
                type: AssetTypes.ACTIVITY,
                id: assetId,
                locale,
                serviceVersion: 'v2',
            });
        }
    }, [resource, localization, locale, currentVersionStatus]);
    const headerOptions = [];
    if (isTranslationV2Enabled()) {
        headerOptions.push({
            name: translate('Export'),
            onClick: handleExportV2,
            variant: 'primary',
            disabled: isExportingV2,
            isLoading: isExportingV2,
        });
    }
    else {
        headerOptions.push({
            name: translate('Export'),
            onClick: () => handleExport(),
            variant: 'primary',
            disabled: isStudyinDraft || isFetching,
            isLoading: isFetching,
        });
    }
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: _jsxs("div", { className: 'flex items-center justify-center gap-x-2', children: [_jsx(H3, { children: locale }), localizationOrigin && showAIAssistedBadge && (_jsx(Tooltip, { title: translate('This translation was created with the assistance of AI'), placement: 'end', children: _jsx(Badge, { variant: 'common', label: translate('Assisted by AI') }) }))] }), breadcrumbPages: breadcrumbPages, options: headerOptions, LinkComponent: RouterLink, customOptions: locale !== Locale.en_US && resource ? (_jsx(ButtonWithTooltip, { variant: 'primary', onClick: () => { }, label: translate('Change status'), disabled: true, tooltip: translate('Status changes for activities can only be performed from the library'), enableTooltip: true, tooltipPlacement: 'bottom' })) : (_jsx("span", {})) }), _jsx(AIGeneratedBanner, { status: currentVersionStatus?.status, origin: currentVersionStatus?.origin }), !isBaseLocaleInSync && (_jsx("div", { className: 'mb-6', children: _jsx(TranslationsOutOfSyncBanner, {}) })), _jsxs("div", { className: 'flex flex-col gap-6', children: [isLoading && _jsx(Spinner, { wrapper: 'full' }), _jsx(AssetsInfoPanel, { listItems: info, locale: locale, title: translate('Activity Info'), version: localeVersion, localizationVersion: currentVersionStatus }), _jsx("hr", { className: 'mb-6' }), _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { size: 'sm', children: translate('Device size') }), _jsx(DeviceSelector, { devices: deviceList, defaultDevice: defaultDevice, showDimensions: true, width: 'lg' })] })] }), resource && !isLocalizationLoading && (_jsx(ActivityReviewScreenList, { activity: resource, localeVersion: currentVersionStatus?.version || Number(localeVersion) }))] }));
};
